import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, CheckCircle, AlertTriangle, AlertOctagon, Timer,
  ChevronDown, ChevronUp, Save, Loader2
} from 'lucide-react';
import { otaAPI } from '../services/api';

const STATUS_CONFIG = {
  expected: { label: 'Expected', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500', icon: Clock, bar: 'bg-emerald-500' },
  urgent: { label: 'Urgent', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500', icon: AlertTriangle, bar: 'bg-amber-500' },
  critical: { label: 'Critical', color: 'bg-red-100 text-red-700 border-red-200', dot: 'bg-red-500', icon: AlertOctagon, bar: 'bg-red-500' },
  completed: { label: 'Completed', color: 'bg-teal-100 text-teal-700 border-teal-200', dot: 'bg-teal-500', icon: CheckCircle, bar: 'bg-teal-500' },
};

const CHECKLIST_ITEMS = [
  { key: 'hotel_id_added', label: 'Hotel ID Added' },
  { key: 'listing_url_submitted', label: 'Listing URL Submitted' },
  { key: 'property_name_verified', label: 'Property Name Verified' },
  { key: 'access_to_hotel', label: 'Access to Hotel Granted' },
  { key: 'photos_verified', label: 'Photos Uploaded/Verified' },
  { key: 'pricing_configured', label: 'Pricing Configured' },
  { key: 'amenities_added', label: 'Amenities Added' },
  { key: 'policies_added', label: 'Policies Added' },
  { key: 'qc_completed', label: 'QC Completed' },
];

// Compact badge-only view (for tables / list rows)
export const OTAChecklistBadge = ({ sla }) => {
  if (!sla) return null;
  const config = STATUS_CONFIG[sla.status] || STATUS_CONFIG.expected;
  const Icon = config.icon;
  const pct = sla.total > 0 ? Math.round((sla.completed / sla.total) * 100) : 0;

  return (
    <div className="flex items-center gap-2" data-testid="ota-checklist-badge">
      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${config.color}`}>
        <Icon size={10} />
        {config.label}
      </span>
      <span className="text-[10px] text-gray-500">{sla.completed}/{sla.total}</span>
      <div className="w-12 h-1.5 bg-gray-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${config.bar}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

// Full editable checklist panel
const OTAChecklistSLATracker = ({ otaId, initialSla, onUpdate, readOnly = false }) => {
  const [sla, setSla] = useState(initialSla || null);
  const [loading, setLoading] = useState(!initialSla);
  const [expanded, setExpanded] = useState(false);
  const [localChecklist, setLocalChecklist] = useState({});
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  const fetchSLA = useCallback(async () => {
    try {
      const res = await otaAPI.getChecklistSLA(otaId);
      setSla(res.data);
      setLocalChecklist(res.data.checklist || {});
    } catch { /* ignore */ }
    setLoading(false);
  }, [otaId]);

  useEffect(() => {
    if (initialSla) {
      setSla(initialSla);
      setLocalChecklist(initialSla.checklist || {});
      setLoading(false);
    } else {
      fetchSLA();
    }
  }, [otaId, initialSla, fetchSLA]);

  const toggleItem = (key) => {
    if (readOnly || sla?.status === 'completed') return;
    setLocalChecklist(prev => {
      const current = prev[key] || 'pending';
      return { ...prev, [key]: current === 'complete' ? 'pending' : 'complete' };
    });
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await otaAPI.updateChecklist(otaId, localChecklist);
      setSla(res.data);
      setLocalChecklist(res.data.checklist || {});
      setDirty(false);
      if (onUpdate) onUpdate(res.data);
    } catch (err) {
      console.error('Failed to save checklist:', err);
    }
    setSaving(false);
  };

  if (loading) return <div className="flex items-center gap-2 py-2"><Loader2 size={14} className="animate-spin text-teal-500" /><span className="text-xs text-gray-400">Loading checklist...</span></div>;
  if (!sla) return null;

  const config = STATUS_CONFIG[sla.status] || STATUS_CONFIG.expected;
  const Icon = config.icon;
  const pct = sla.total > 0 ? Math.round((sla.completed / sla.total) * 100) : 0;

  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden" data-testid="ota-checklist-sla-tracker">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors"
        data-testid="ota-checklist-toggle"
      >
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${config.color}`}>
            <Icon size={13} />
            {config.label}
          </span>
          {sla.status !== 'completed' && (
            <span className="inline-flex items-center gap-1 text-xs text-gray-500">
              <Timer size={12} />
              {sla.timer_display}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs font-medium text-gray-600">{sla.completed}/{sla.total}</span>
          <div className="w-20 h-2 bg-gray-200 rounded-full overflow-hidden">
            <div className={`h-full rounded-full transition-all duration-500 ${config.bar}`} style={{ width: `${pct}%` }} />
          </div>
          {expanded ? <ChevronUp size={14} className="text-gray-400" /> : <ChevronDown size={14} className="text-gray-400" />}
        </div>
      </button>

      {/* Expandable Items */}
      {expanded && (
        <div className="p-4 space-y-2" data-testid="ota-checklist-items">
          {CHECKLIST_ITEMS.map(({ key, label }) => {
            const isComplete = (localChecklist[key] || 'pending') === 'complete';
            return (
              <div
                key={key}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border transition-all cursor-pointer ${
                  isComplete
                    ? 'bg-teal-50 border-teal-200'
                    : 'bg-white border-gray-100 hover:border-gray-200'
                } ${readOnly || sla.status === 'completed' ? 'cursor-default' : ''}`}
                onClick={() => toggleItem(key)}
                data-testid={`ota-checklist-item-${key}`}
              >
                <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center shrink-0 transition-all ${
                  isComplete ? 'bg-teal-500 border-teal-500' : 'border-gray-300'
                }`}>
                  {isComplete && <CheckCircle size={12} className="text-white" />}
                </div>
                <span className={`text-sm ${isComplete ? 'text-teal-700 font-medium' : 'text-gray-700'}`}>
                  {label}
                </span>
              </div>
            );
          })}

          {!readOnly && sla.status !== 'completed' && dirty && (
            <button
              onClick={handleSave}
              disabled={saving}
              className="w-full flex items-center justify-center gap-2 mt-3 px-4 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 text-sm font-semibold"
              data-testid="ota-checklist-save-btn"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save Checklist
            </button>
          )}

          {sla.status !== 'completed' && sla.pending_items.length > 0 && !dirty && (
            <p className="text-xs text-gray-400 pt-1">
              Pending: {sla.pending_items.slice(0, 4).join(', ')}
              {sla.pending_items.length > 4 && ` +${sla.pending_items.length - 4} more`}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default OTAChecklistSLATracker;
